import {useTranslation} from '@aboutyou/i18n';
import {useVipLandingPageRoute} from '@aboutyou/router';
import {
  Button,
  COLORS,
  FONT_SIZES,
  FONT_WEIGHTS,
  GenericIconName,
  Icon,
  IconName,
  Text,
  UnstyledLink,
  desktopAndUp,
  higherDesktopAndUp,
} from '@aboutyou/ui-components';
import {useCustomerIsVip} from '@aboutyou/user';
import React from 'react';
import styled, {css} from 'styled-components';
import {useLogoutHandler} from '../../../../features/Authentication/hooks/useLogoutHandler';
import {useShowBandData} from '../hooks/useShowBankData';

export const FlyoutLoggedIn: React.FC<{
  enableBankDataRequest: boolean;
  onLogout: VoidFunction;
}> = ({enableBankDataRequest, onLogout}) => {
  const {t} = useTranslation();
  const {logout} = useLogoutHandler();
  const isVip = useCustomerIsVip();
  const vipLandingPageRoute = useVipLandingPageRoute();
  const showBankData = useShowBandData(enableBankDataRequest);

  const bankDataItem = showBankData
    ? {
        label: t('Bank information'),
        icon: GenericIconName.CreditCard,
        href: '/a/bank-data',
      }
    : undefined;

  const vipItem = isVip
    ? {
        label: t('Your VIP benefits'),
        icon: GenericIconName.StarOutline,
        href: vipLandingPageRoute,
      }
    : undefined;

  const navItems = [
    {
      label: t('Your orders'),
      icon: GenericIconName.UspFreeShipping,
      href: '/a/orders',
    },
    {...bankDataItem},
    {
      label: t('Help'),
      icon: GenericIconName.Help,
      href: '/h',
    },
    {
      label: t('Profile & security'),
      icon: GenericIconName.Lock,
      href: '/a/profile',
    },
    {
      label: t('Favourites'),
      icon: GenericIconName.Heart,
      href: '/a/favourites',
    },
    {
      label: t('Settings'),
      icon: GenericIconName.Settings,
      href: '/a/settings',
    },

    {...vipItem},
  ];

  return (
    <Container>
      {navItems.map(({href, icon, label}, key) => {
        if (!(href && icon && label)) {
          return null;
        }

        return (
          <NavItem
            key={`nav_item${key}`}
            label={label}
            icon={icon as IconName}
            href={href}
          />
        );
      })}

      <HorizontalRuler />

      <Button
        data-testid="LogoutButton"
        type="secondary"
        onClick={() => {
          logout();
          onLogout();
        }}
      >
        {t('Log out')}
      </Button>
    </Container>
  );
};

const NavItem: React.FC<{
  label: string;
  icon: IconName;
  href?: string;
  onClick?: VoidFunction;
}> = ({label, icon, href = '', onClick}) => {
  const navItemContent = (
    <>
      <NavItemIcon asset={icon} size="S" />
      <NavItemLabel>{label}</NavItemLabel>
    </>
  );

  const dataTestId = `NavLabel_${label}`;

  if (href) {
    return (
      <NavItemLink to={href} data-testid={dataTestId} aria-label={undefined}>
        {navItemContent}
      </NavItemLink>
    );
  }

  return (
    <NavItemButton onClick={onClick} data-testid={dataTestId}>
      {navItemContent}
    </NavItemButton>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
`;

const HorizontalRuler = styled.hr`
  border-top: 0px;
  box-shadow: inset 0 -1px 0 0 ${COLORS.Grey3};
`;

const NavItemIcon = styled(Icon)`
  fill: ${COLORS.Grey1};
  color: ${COLORS.Grey1};
`;

const NavItemLabel = styled(Text)`
  font-size: ${FONT_SIZES.px14};
  font-weight: ${FONT_WEIGHTS.mediumBold};
`;

const navItemStyles = css`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 180px;
  height: 48px;
  padding: 10px;

  ${desktopAndUp(css`
    width: 190px;
  `)}

  ${higherDesktopAndUp(css`
    width: 210px;
  `)}

  :hover {
    ${NavItemIcon} {
      fill: ${COLORS.Black};
      color: ${COLORS.Black};
    }

    ${NavItemLabel} {
      font-weight: ${FONT_WEIGHTS.heavy};
    }
  }
`;

const NavItemLink = styled(UnstyledLink)`
  ${navItemStyles}
`;

const NavItemButton = styled.button`
  ${navItemStyles}
`;
